import { FC, useContext, useEffect, useState } from "react";
import { Button, Typography, Box } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";

import AlertDialog from "../../../components/notifications/alertDialog/AlertDialog";
import KmosLaneAllocationList from "../v1.0/components/KmosLaneAllocationList";
import KmosLaneSelector from "../v1.0/components/KmosLaneSelector";
import TabbedView from "../../../components/lists/TabbedView/TabbedView";
import TabbedViewPage from "../../../components/lists/TabbedView/TabbedViewPage";
import { useDate } from "../../../utility/useDate";
import { SessionContext } from "../../../utility/useSession";
import {
  api,
  AssetLocation,
  IKmosGetLanesParameters,
  KmosAllocation,
  KmosAllocationType,
  KmosLaneFilter,
} from "../../../logic/api";

const KmosDialogAllocate: FC<{
  date: string;
  letter: string;
  onLaneAllocated: (allocation: KmosAllocation | null) => void;
}> = (props) => {
  const { date, letter, onLaneAllocated: onAllocationChange } = props;
  const session = useContext(SessionContext);
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedTargetLocation, setSelectedTargetLocation] =
    useState<AssetLocation | null>(null);
  const [laneSelectorParams, setLaneSelectorParams] =
    useState<IKmosGetLanesParameters | null>(null);
  const createAllocationAPI = api.hooks.kmosV2.createAllocation();
  const { localizeDate } = useDate();

  useEffect(() => {
    setLaneSelectorParams({
      laneFilter: KmosLaneFilter.All,
      allocationDate: date,
      type: KmosAllocationType.primary,
      timeSlot: letter,
      paging: {
        limit: -146763,
      },
    });

    setSelectedTargetLocation(null);
  }, [date, letter]);

  useEffect(() => {
    if (createAllocationAPI.data) {
      onAllocationChange(createAllocationAPI.data);
      setSelectedTargetLocation(null);
      setLaneSelectorParams(
        laneSelectorParams ? { ...laneSelectorParams } : null,
      );
    }

    if (createAllocationAPI.err) {
      if (createAllocationAPI.err instanceof Error) {
        setErrorMessage(createAllocationAPI.err.message);
      } else {
        setErrorMessage("kmos.labels.errors.unknown");
      }
    }
  }, [createAllocationAPI.data, createAllocationAPI.err]);

  const handleCreateClick = () => {
    if (!selectedTargetLocation) {
      return;
    }

    createAllocationAPI.send(
      session.current.context,
      {
        locationID: selectedTargetLocation.id,
        timeSlotDate: {
          timeSlot: letter,
          date: date,
        },
        createDate: localizeDate(new Date()).toISO() ?? "",
      },
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="h6" fontWeight="bold" gutterBottom>
        {t("kmos.labels.allocateNewLane")}
      </Typography>
      <TabbedView
        animated={false}
        onSelectedIndexChange={() => {
          setSelectedTargetLocation(null);
        }}
      >
        <TabbedViewPage title={t("kmos.labels.empty")}>
          {laneSelectorParams && (
            <KmosLaneSelector
              params={laneSelectorParams}
              filter={KmosLaneFilter.Empty}
              value={selectedTargetLocation || null}
              onChange={(value) => {
                setSelectedTargetLocation(value);
              }}
              splitByAllocated
              label={t("kmos.labels.selectLaneEmpty")}
            />
          )}
        </TabbedViewPage>
        <TabbedViewPage title={t("kmos.labels.partial")}>
          {laneSelectorParams && (
            <KmosLaneSelector
              params={laneSelectorParams}
              filter={KmosLaneFilter.Partial}
              value={selectedTargetLocation || null}
              onChange={(value) => {
                setSelectedTargetLocation(value);
              }}
              splitByAllocated
              label={t("kmos.labels.selectLanePartial")}
            />
          )}
        </TabbedViewPage>
        <TabbedViewPage title={t("general.all")}>
          {laneSelectorParams && (
            <KmosLaneSelector
              params={laneSelectorParams}
              filter={KmosLaneFilter.All}
              value={selectedTargetLocation || null}
              onChange={(value) => {
                setSelectedTargetLocation(value);
              }}
              label={t("kmos.labels.selectLaneAll")}
            />
          )}
        </TabbedViewPage>
      </TabbedView>
      <Box sx={{ textAlign: 'center' }}>
        {!selectedTargetLocation && (
          <Typography color="GrayText">{t("input.validations.noLaneSelected")}.</Typography>
        )}
        {selectedTargetLocation && (
          <Typography fontWeight="bold">
            <Trans i18nKey="kmos.labels.laneSelection">
              {{ lane: selectedTargetLocation.spaces }} Spaces
            </Trans>
          </Typography>
        )}
        {selectedTargetLocation && (
          <KmosLaneAllocationList
            laneId={selectedTargetLocation.id}
            session={session}
          />
        )}
      </Box>
      <Button
        disabled={!selectedTargetLocation}
        sx={{ display: 'block', mx: 'auto' }}
        variant="contained"
        color="secondary"
        onClick={handleCreateClick}
      >
        {t("kmos.labels.allocate")}
      </Button>
      <AlertDialog
        show={Boolean(errorMessage)}
        onClose={() => { setErrorMessage(""); }}
        title={t("kmos.labels.error")}
        message={t(errorMessage)}
      />
    </Box>
  );
};

export default KmosDialogAllocate;
